
// ============================================================================
// SQL AST ENGINE — CIRCULAR-SAFE VERSION (V50 + APPLY FIXED)
// ============================================================================

import {
  SqlField,
  SqlFn,
  SqlLiteral,
  SqlExp,
  SqlExpChain,
  SqlSubQuery,
  SqlSubQueryBuilder,
  SqlApplyColumn,
  SqlCteBuilder,
  SqlRecursiveCteBuilder,
  SqlNavHybrid
} from "./sql-core";

export class SqlAst {
  selects: any[] = [];
  joins: any[] = [];
  wheres: any[] = [];
  havings: any[] = [];
  groups: any[] = [];
  orders: any[] = [];
  ctes: any[] = [];
  applies: any[] = [];
  unions: any[] = [];
  skip = 0;
  take = 200;

  constructor(public baseTable: string, public alias: string) {}

  // ========================================================================
  // HELPERS
  // ========================================================================
  private fmt(col: string) {
    if (!col) return col;
    col = col.replace(/\\[/g, "").replace(/\\]/g, "");
    if (col.includes(".")) {
      const [a, c] = col.split(".");
      return `[${a}].[${c}]`;
    }
    return `[${this.alias}].[${col}]`;
  }

  private resolve(f: SqlField) {
    return this.fmt(`${f.alias}.${f.name}`);
  }

  private wrapRow(alias: string) {
    return new Proxy(
      {},
      {
        get: (_: any, prop: string) => new SqlField(alias, prop)
      }
    );
  }

  // ========================================================================
  // SELECT
  // ========================================================================
  addSelectObject(obj: any) {
    for (const alias of Object.keys(obj)) {
      const v = obj[alias];

      if (v instanceof SqlField) {
        this.selects.push({
          columnName: this.resolve(v),
          alias,
          isTrusted: true
        });
        continue;
      }

      if (v instanceof SqlFn) {
        this.selects.push({
          columnName: v.toObject(),
          alias,
          isTrusted: false
        });
        continue;
      }

      if (v instanceof SqlLiteral) {
        this.selects.push({
          columnName: v.value,
          alias,
          isTrusted: true
        });
        continue;
      }

      if (v instanceof SqlSubQuery) {
        const sb = new SqlSubQueryBuilder();
        const built = v.builder(sb).build();
        this.selects.push({
          columnName: built,
          alias,
          isTrusted: false
        });
        continue;
      }

      if (v instanceof SqlApplyColumn) {
        this.selects.push({
          columnName: { type: "ApplyColumn", name: v.name },
          alias,
          isTrusted: false
        });
        continue;
      }
    }
  }

  // ========================================================================
  // WHERE
  // ========================================================================
  addWhere(e: SqlExp | SqlExpChain) {
    this.wheres.push(this.compileExp(e));
  }

  addOr(e: SqlExp | SqlExpChain) {
    const exp = this.compileExp(e);

    if (!this.wheres.length) {
      this.wheres.push({ logic: "Or", subFilters: [exp], isTrusted: false });
      return;
    }

    const last = this.wheres[this.wheres.length - 1];
    if (last.logic === "Or") last.subFilters.push(exp);
    else {
      this.wheres.push({
        logic: "Or",
        subFilters: [exp],
        isTrusted: false
      });
    }
  }

  // ========================================================================
  // CTE
  // ========================================================================
  with(name: string, fn: (b: SqlCteBuilder) => any) {
    const b = new SqlCteBuilder(name);
    const built = fn(b).build();
    this.ctes.push({
      name,
      query: built,
      isRecursive: false,
      isTrusted: true
    });
    return this;
  }

  withRecursive(name: string, fn: (b: SqlRecursiveCteBuilder) => any) {
    const b = new SqlRecursiveCteBuilder(name);
    const built = fn(b).build();
    this.ctes.push({
      name,
      query: built,
      isRecursive: true,
      isTrusted: true
    });
    return this;
  }

  // ========================================================================
  // JOIN
  // ========================================================================
  addJoin(f: SqlField | SqlNavHybrid, type = "INNER") {
    const field = f instanceof SqlNavHybrid ? f.asSqlField() : f;
    const joinTable = field.navRootTable || field.alias;
    const joinAlias = field.joinAlias;

    const left = `[${field.alias}].[${field.name}]`;
    const right = `[${joinAlias}].[${field.navColumnName}]`;

    this.joins.push({
      joinType: type,
      table: joinTable,
      alias: joinAlias,
      on: `${left} = ${right}`,
      isTrusted: true
    });

    return this;
  }

  // ========================================================================
  // JOIN CTE
  // ========================================================================
  joinCTE(name: string, alias: string, fn: any) {
    const l = this.wrapRow(this.alias);
    const r = this.wrapRow(alias);

    this.joins.push({
      joinType: "INNER",
      table: name,
      alias,
      on: this.compileExp(fn(l, r)),
      isTrusted: true
    });

    return this;
  }

  fullJoinCTE(name: string, alias: string, fn: any) {
    const l = this.wrapRow(this.alias);
    const r = this.wrapRow(alias);

    this.joins.push({
      joinType: "FULL",
      table: name,
      alias,
      on: this.compileExp(fn(l, r)),
      isTrusted: true
    });

    return this;
  }

  // ========================================================================
  // APPLY — CIRCULAR-SAFE
  // ========================================================================
  private flattenSubQuery(raw: any) {
    return {
      type: "SubQuery",
      selects: raw.selects ?? [],
      from: raw.from ?? null,
      where: raw.where ?? [],
      group: raw.group ?? [],
      order: raw.order ?? [],
      limit: raw.limit ?? null,
      offset: raw.offset ?? null
    };
  }

  apply(name: string, fn: (b: SqlSubQueryBuilder) => SqlSubQueryBuilder) {
    return {
      type: "Apply",
      name,
      alias: name,
      build: () => {
        const sb = new SqlSubQueryBuilder();
        const builder = fn(sb);
        return builder.build();
      }
    };
  }

  crossApply(app: any) {
    if (!app || !app.build) {
      throw new Error("crossApply() requires apply() descriptor");
    }

    const raw = app.build();
    const flat = this.flattenSubQuery(raw);

    this.applies.push({
      type: "CrossApply",
      name: app.name,
      alias: app.alias,
      query: flat,
      isTrusted: true
    });

    return this;
  }

  applyColumn(name: string, fn: Function) {
    return new SqlApplyColumn(name, fn);
  }

  // ========================================================================
  // GROUP
  // ========================================================================
  addGroup(f: SqlField[]) {
    for (const col of f) this.groups.push(this.resolve(col));
  }

  // ========================================================================
  // HAVING
  // ========================================================================
  addHaving(e: SqlExp | SqlExpChain) {
    const h = this.compileExp(e);
    h.column = this.fmt(h.column);
    this.havings.push(h);
  }

  // ========================================================================
  // ORDER
  // ========================================================================
  addOrder(arr: any[]) {
    for (const o of arr) {
      if (o instanceof SqlField) {
        this.orders.push({ column: this.resolve(o), desc: false });
        continue;
      }

      if (typeof o === "string") {
        const [raw, dirRaw] = o.split(" ");
        const desc = (dirRaw || "ASC").toUpperCase() === "DESC";

        let col: string;

        if (raw.startsWith("{")) col = raw;
        else if (raw.includes(".")) col = this.fmt(raw);
            else col = this.fmt(`${ this.alias}.${ raw}`);

            this.orders.push({ column: col, desc });
        }
    }
}

// ========================================================================
// UNION
// ========================================================================
union(fn: any) {
    const sub = new SqlAst(this.baseTable, this.alias);
    fn(sub);
    this.unions.push({
    type: "UNION",
      query: sub.toQueryDescriptor(),
      isTrusted: true
    });
    return this;
}

unionAll(fn: any) {
    const sub = new SqlAst(this.baseTable, this.alias);
    fn(sub);
    this.unions.push({
    type: "UNION ALL",
      query: sub.toQueryDescriptor(),
      isTrusted: true
    });
    return this;
}

// ========================================================================
// EXPRESSION COMPILER
// ========================================================================
private compileExp(e: any): any {
    if (e instanceof SqlExp) {
      let col: any;

if (e.left instanceof SqlField) col = this.resolve(e.left);
      else if (e.left instanceof SqlFn) col = e.left.toObject();
      else if (e.left instanceof SqlLiteral) col = e.left.value;
      else if (e.left instanceof SqlSubQuery) {
    const b = new SqlSubQueryBuilder();
    col = e.left.builder(b).build();
} else col = this.fmt(String(e.left));

return {
column: col,
        operator: e.op,
        value: e.right,
        isTrusted: false,
        subFilters: []
      }
;
    }

    if (e instanceof SqlExpChain) {
    return {
    logic: e.logic === "AND" ? "And" : "Or",
        isTrusted: false,
        subFilters: e.exps.map(x => this.compileExp(x))
      }
    ;
}

throw new Error("Invalid SQL expression");
  }

  // ========================================================================
  // OUTPUT
  // ========================================================================
  toQueryDescriptor() {
    const whereFinal =
      this.wheres.length === 1 && this.wheres[0].logic
        ? this.wheres
        : [
            {
    logic: "And",
              subFilters: this.wheres,
              isTrusted: false
            }
          ];

    const auto = new Set<string>();
    for (const s of this.selects)
      if (typeof s.columnName === "string")
        auto.add(this.fmt(s.columnName));
    for (const g of this.groups) auto.add(this.fmt(g));

    return {
    baseTable: this.baseTable,
      baseAlias: this.alias,
      selects: this.selects,
      joins: this.joins,
      where: whereFinal,
      having: this.havings,
      sorts: this.orders,
      groupBy: Array.from(auto),
      skip: this.skip,
      take: this.take,
      ctes: this.ctes,
      unions: this.unions,
      applies: this.applies,
      options: { useCache: false, strictMode: false }
    }
    ;
}
}
